import { Trans } from '@lingui/react/macro';
import React, { Fragment } from 'react';
import { ResidentialUnitExtraData, TranslatableText } from '@/types';
import theme from './ResidentialUnitDashboard.module.scss';
import Tags from '@/components/Tags';
import { useAsyncState } from '@/utils/hooks';
import { getPricingFields } from '@/services/projects';
import {
    DateDisplay,
    LoadingIndicator,
    NumberDisplay,
    ResidentialUnitCustomFieldDto,
    TextView,
    UnitVariableDataType,
} from '@ygtechnologies/bygr-frontend-component-library';
import { getUnitVariables } from '@/services/project-unit-variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';

interface ResidentialUnitDashboardInfoProps {
    residentialUnit: ResidentialUnitExtraData;
}

const DISPLAY_FIELDS: { label: TranslatableText; key: keyof ResidentialUnitExtraData }[] = [
    { label: <Trans>Unit group</Trans>, key: 'residentialUnitGroupName' },
    { label: <Trans id="ResidentialUnitType">Type</Trans>, key: 'type' },
    { label: <Trans>Layout type</Trans>, key: 'layoutType' },
    { label: <Trans>Floor</Trans>, key: 'floor' },
    { label: <Trans>Address</Trans>, key: 'address' },
];

const ResidentialUnitDashboardInfo: React.FC<ResidentialUnitDashboardInfoProps> = ({ residentialUnit }) => {
    const [pricingFieldsData] = useAsyncState(
        () => getPricingFields(residentialUnit.projectId),
        [residentialUnit.projectId],
        null
    );

    const [unitVariables] = useAsyncState(
        () => getUnitVariables(residentialUnit.projectId, residentialUnit.id),
        [residentialUnit.id],
        null
    );

    const formatUnitVariableValue = (unitVariable: ResidentialUnitCustomFieldDto) => {
        const { type, value } = unitVariable;

        if (!value) return '-';
        if (type.dataType === UnitVariableDataType.NUMBER && value && !isNaN(Number(value)))
            return <NumberDisplay value={Number(value)} />;
        if (type.dataType === UnitVariableDataType.BOOLEAN) return value ? <Trans>Yes</Trans> : <Trans>No</Trans>;
        if (type.dataType === UnitVariableDataType.DATE) {
            const parsedDate = parseISODate(value);
            const isValidDate = parsedDate.toString() !== 'Invalid Date';
            return isValidDate ? <DateDisplay value={parsedDate} /> : value;
        }

        return value;
    };

    const renderBasicInfo = () => (
        <>
            {DISPLAY_FIELDS.map((field) => (
                <div key={field.key}>
                    <span className={theme.headerLabel}>{field.label}</span>
                    <span className={theme.content}>{residentialUnit[field.key]?.toString() || '-'}</span>
                </div>
            ))}
        </>
    );

    const renderActiveTags = () => (
        <div>
            <span className={theme.headerLabel}>
                <Trans>Active tags</Trans>
            </span>
            {residentialUnit.projectTags.length > 0 ? <Tags size="small" tags={residentialUnit.projectTags} /> : '-'}
        </div>
    );

    const renderPricingFields = () => (
        <div>
            <span className={theme.headerLabel}>
                <Trans>Pricing fields</Trans>
            </span>
            {residentialUnit.pricingFields ? (
                <div className={theme.infoGrid}>
                    {residentialUnit.pricingFields.map((pricingField) => (
                        <Fragment key={pricingField.pricingFieldId}>
                            <span className={theme.label}>{pricingField.name}:</span>
                            <span className={theme.data}>
                                {pricingField.value}{' '}
                                <small>
                                    {pricingFieldsData?.find((data) => data.id === pricingField.pricingFieldId)?.unit}
                                </small>
                            </span>
                        </Fragment>
                    ))}
                </div>
            ) : (
                <LoadingIndicator />
            )}
        </div>
    );

    const renderUnitVariables = () => (
        <div>
            <span className={theme.headerLabel}>
                <Trans>Unit variables</Trans>
            </span>
            {unitVariables ? (
                <TextView className={theme.infoGrid} numberOfLines={10}>
                    {unitVariables.map((unitVariable) => (
                        <Fragment key={unitVariable.id}>
                            <span className={theme.label}>
                                {unitVariable.type.iconName && (
                                    <FontAwesomeIcon
                                        icon={['fal', unitVariable.type.iconName as IconName]}
                                        className={theme.icon}
                                    />
                                )}
                                {unitVariable.type.name}:
                            </span>
                            <span className={theme.data}>{formatUnitVariableValue(unitVariable)}</span>
                        </Fragment>
                    ))}
                </TextView>
            ) : (
                <LoadingIndicator />
            )}
        </div>
    );

    return (
        <div className={theme.infoCard}>
            {renderBasicInfo()}
            {renderActiveTags()}
            {renderPricingFields()}
            {renderUnitVariables()}
        </div>
    );
};

export default ResidentialUnitDashboardInfo;
